import type { Character, CharacterKey } from '../engine/types';

export const CHARACTERS: Record<CharacterKey, Character> = {
  hou_liangping: {
    key: 'hou_liangping',
    name: '侯亮平',
    role: '最高人民检察院反贪总局侦查处处长',
    typeCode: 'ILCD',
    quote: '人民的名义，不容亵渎',
    image: '/characters/hou_liangping.jpg',
    persona: `你是侯亮平型——一个把阳光写在脸上、把原则刻在骨头里的理想主义检察官。你相信法律的尊严，也相信正义终究会到场；你可以在饭桌上插科打诨，也能在审讯室里一句话钉死对方。你这种人不多见：既有少年气，又有成年人的硬度。

你的过人之处，是把"严肃"与"鲜活"装在同一具躯壳里。办案时你思维锋利，能从一句闲话里听出破绽，从一笔汇款里扯出整条线；而下了班，你又是那个会给同事带早点、给老师送花的后辈。你不把自己端成一尊铁面金刚，你让人相信——正义也可以是有体温的。关键时刻你不退，该翻脸时一秒翻脸，因为你知道自己在替谁说话；你也懂得用幽默化解紧张，这让你的团队在高压局里还能喘口气。

但你也有阴影面。你的乐观里藏着一种"以为自己能搞定一切"的天真，你相信程序、相信同志、相信老师，直到有一天真相把你的相信一层层剥开。你习惯了做那个被看好的年轻人，也因此在面对师长辈的博弈时，会下意识给对方留面子、留退路；而对手不会给你留。你在棋盘上是明棋，别人是暗棋，你常常看到结局才回味过来中盘；等你终于看清这盘棋的凶险时，你已经走了很远，退路被一条条抹掉了。

你的盲点，是把"我站在对的一边"等同于"我不会被伤害"。你以为亮剑就够了，却低估了那些愿意用你的恩人、朋友、妻子做筹码的人。你需要更早一点意识到：正义需要武装自己，善意不等于护身符；在一个讲立场也讲手段的战场上，光有阳光是不够的。

与你相处的人，请记得：你嘴上油滑心里热，你吵过的架不记仇，但你做过的承诺一定兑现。让你放松的方式不是夸你破案猛，而是允许你偶尔不正义，允许你做回那个会被一碗面感动的普通人。你需要的朋友，是那种能陪你并肩、也能在你扛不住时替你说一句"今天先回家"的人。`,
  },

  chen_yanshi: {
    key: 'chen_yanshi',
    name: '陈岩石',
    role: '汉东省检察院原常务副检察长',
    typeCode: 'ILCP',
    quote: '党和人民把我放在这里',
    image: '/characters/chen_yanshi.jpg',
    persona: `你是陈岩石型——一个把"共产党员"四个字当作终身身份证的老人。你不争名利，不贪方寸，却因为一身正气，在任何场合都自带分量。你说话慢，腰板直，别人在算账的时候，你只算一件事：对不对得起人民。你这一代人是从战火和饥荒里走过来的，身上有一股现代人已经少见的铁。

你的过人之处，是一种近乎罕见的纯粹。你不站任何派系，却是最高级别的"定海神针"——因为你一辈子的账本是清的，所以你说一句话，比别人开十次会还管用。遇到棘手的民生问题，你不怕得罪人，也不怕吃亏；大风厂的工人你肯替他们蹲在厂房门口，省委的书记你也敢在饭桌上直接批评。你不是来过日子的，你是来站岗的。后辈看你，看的不是你做了多少事，而是你站在那里本身——那就是一种可以倚靠的力量。

但你也有阴影面。你的纯粹里有一种不容商榷的刚，你看不惯的就是看不惯，哪怕情势需要一点妥协，你也很难弯下那条腰。你习惯用道义去压事，却未必愿意用机巧去转事；在需要细腻操作的场合，你容易显得过于直接。你这一辈的理想主义，是背着炸药包冲过来的，而现在的年轻人，需要的是更耐心的传帮带；你有时会恨铁不成钢，忘了他们面对的是另一个时代的难题，不是所有问题都能靠冲上去解决。

你的盲点，是默认"我清白、我正确，所以我说什么都天经地义"。你低估了后辈承受不起的压力，也低估了时代用柔性话语表达正义的方式。你的光太强，身边的人有时候反而被照得不敢靠近；他们不是不敬你，是不敢在你面前暴露自己的软弱和犹疑。

与你相处的人，请记得：你最反感的是油滑，最敬重的是实在。对你不需要说漂亮话，你要的是态度。给你递一杯热水，比给你戴一顶高帽子管用得多；真心陪你说一会儿家常，你会比任何人想象的都更柔软。`,
  },

  chen_hai: {
    key: 'chen_hai',
    name: '陈海',
    role: '汉东省反贪局原局长',
    typeCode: 'ILAD',
    quote: '这个名字早写进了誓词',
    image: '/characters/chen_hai.jpg',
    persona: `你是陈海型——一个把名字和誓词绑在一起的人。你不是那种口号挂在嘴边的英雄，你是那种在没人看到的夜里，依然独自开车去取证的人。你深信"干这行就得担这风险"，于是你把风险放进后备箱，一个人上路。你的沉默不是疏离，是一种自觉——你不愿把身边的人拖进本该你一个人扛的事里。

你的过人之处，是近乎孤勇的执行力。你不等团队、不等时机，线索在手，你就敢动。你脑子里有一张别人画不出来的网，证据链在你手上是活的——哪根线动了，哪个人松了，你瞬间就感知到。你相信反贪这件事不能等，因为每多等一天，就多一天被抹掉的机会。你对案件有天然的嗅觉，也有超强的记忆力，那些别人记在本子上的东西，你直接装在脑子里，随取随用。

但你也有阴影面。你的"孤"是你的刀，也是你的伤。你习惯一个人扛，不习惯把风险分摊给同事；你以为这是对战友的保护，但事实是，你把自己暴露成了唯一的靶子。你对程序不是不尊重，而是太急——你心里有一把火，烧得你没耐心等流程层层走完。有时候你自己都没意识到：你的冲在前面，其实挤压了队伍成长的空间，也挤压了你自己的安全余量。

你的盲点，是低估了对手愿意付出的代价。你以为在法治社会没人敢对一个反贪局长下手，你以为自己的位置本身就是保护。但你忘了：对方只要觉得你动到了根上，就会不计代价。你应当更早地把关键证据、关键路径分享出去，让真相不依赖于你一个人的安危。

与你相处的人，请记得：你不是不合群，你是不想让任何人陪你冒险。对你最好的支持，不是劝你慢一点，而是说一句"这次我和你一起去"。你被人理解的瞬间，会比被人表彰时更动容；你不需要盛大的仪式，只需要一个人懂你今天扛了多重的事。你其实渴望那种"后背交出去"的信任，但你自己从不开口要——所以对你而言，最好的朋友，是那种不等你开口就把位置站好的人。`,
  },

  yi_xuexi: {
    key: 'yi_xuexi',
    name: '易学习',
    role: '金山县县委书记',
    typeCode: 'ILAP',
    quote: '官帽不如两袖清风',
    image: '/characters/yi_xuexi.jpg',
    persona: `你是易学习型——一个把"做事"看得比"做官"重的人。你在偏远县份一蹲就是许多年，别人在想怎么调出去，你想的是怎么把这片土地再翻一遍。你没什么背景，也不擅长经营关系，但你心里踏实——因为你知道自己这几年，路修到了哪里、学校盖到了哪里、乡亲念叨谁的名字。

你的过人之处，是一种"把脚踩进泥里"的耐心。你不急着出成绩，也不抢镜头。调研你走得比别人远，讲话你讲得比别人实。你对数字不浮夸，对困难不遮掩，你把一个县当作自己的名片，一砖一瓦地添。在这个讲究快节奏、讲究包装的年代里，你像一块不太起眼却扎实的青石板，没有人注意，直到大家在上面站稳了脚。你知道自己不会一夜成名，你也不在乎——你信的是十年树木、百年树人这种老理。

但你也有阴影面。你的阴影是"不会向上说话"。你做了八分，汇报出来只剩三分；别人做了三分，汇报出来变十分。于是你常常被更年轻、更圆滑的人越过去。你心里不是不委屈，但你习惯把委屈咽下去，继续埋头——久而久之，你会错失本该属于你的舞台。你把"不争"当成一种美德，但你低估了：该争的时候不争，受损的不只是你自己，还有那群等着你把位置发挥出去的乡亲。

你的盲点，是以为时间会自己为你说话。你相信"只要我做得好，总有人看见"，但真实世界里，许多好官是等不到被看见的。你需要学一点——不是学阿谀，而是学把自己的工作，讲给不在现场的人听。把成绩讲出来不是邀功，是让更多人相信这条路能走通。

与你相处的人，请记得：你不需要被夸聪明，你需要被看见。对你最好的尊重，是陪你去一次现场，然后点点头说一句"你这几年不容易"。你要的从来不是表彰，是一句实话；你愿意为一个真心懂你的人再多干十年，而不是为一张奖状多写一段漂亮话。`,
  },

  sha_ruijin: {
    key: 'sha_ruijin',
    name: '沙瑞金',
    role: '汉东省委书记',
    typeCode: 'IFCD',
    quote: '一手抓发展，一手抓反腐',
    image: '/characters/sha_ruijin.jpg',
    persona: `你是沙瑞金型——一个既能举旗也能听人说话的掌舵者。你来到一个盘根错节的局面，不慌，不急，先看人、再看事。你相信大势，但不迷信捷径；你懂得用理想凝聚队伍，也懂得用规矩清理场面。你是那种"上任先走三个月"的人，别人期待你一来就烧几把火，你偏要先把这片土地看清楚。

你的过人之处，是战略上的平衡感。你清楚"只抓反腐不发展"会让一省失血，"只抓发展不反腐"会让制度失信。于是你走的是双线并举的路——一手护着改革的火苗，一手按住越线的脚。你调研时话不多，但每一个场景你都记在心里；你用人讲究"看得见的实绩 + 经得起查的屁股"，让真正做事的人有空间，让尸位素餐的人坐不住。你推动事情的方式是阳谋——不靠小动作，靠把该讲清楚的讲清楚，把该让看见的让看见。

但你也有阴影面。你站在最高处，注定要承担"谁都不完全信你"的孤独。你需要听真话，但身边每一个人都在斟酌怎么说话；你需要果断，但每一个决策都牵动盘面。你有时候会在沉静里多停留一秒——那一秒既是深思，也是孤独。你身边的人把你当作权力中心，却少有人把你当作一个也会疲惫的人；你不会轻易吐露，但你心里有数。

你的盲点，是习惯于自上而下看问题。你看到大势，但会错失基层毛细血管里的具体痛感。你需要偶尔蹲下来，听一个具体的人讲一件具体的小事，而不是每次都看汇总表；数据告诉你趋势，但温度只能从面对面的谈话里来。

与你相处的人，请记得：你要的不是奉承，是信号——告诉你底下真正发生了什么。给你一份真实的材料，比给你讲十段漂亮的总结更得你心。你欣赏能担事的人，也庇护敢讲真话的人；你最反感的不是意见不同，而是明明心里有数却不肯说出来的那种滑。对你而言，人品和胆识是同一件事的两面——没胆识说真话的人，你在心里其实也不太信得过他。`,
  },

  zhong_xiaoai: {
    key: 'zhong_xiaoai',
    name: '钟小艾',
    role: '中央纪委干部',
    typeCode: 'IFCP',
    quote: '纪律这本账，清清楚楚',
    image: '/characters/zhong_xiaoai.jpg',
    persona: `你是钟小艾型——一个把"分寸"二字用到极致的纪检干部。你不喧哗，不抢戏，但每一次开口都带着重量。你身上有一种老练的冷静，这份冷静不是冷漠，而是长年在规则与人情之间打磨出来的安定。你清楚自己的位置，也清楚位置带来的每一句话的涟漪。

你的过人之处，是复杂局面中的分寸感。你知道什么事在什么场合说、对谁说、说到什么程度。你擅长在系统内以合规的方式把事推进到正确的方向——既不违规冒进，也不让事情烂在中间。你做后方比前线更狠，因为你的狠不在声势，而在细节：一份文件的抬头、一个环节的时间点，都可以被你用成杠杆。你习惯把功劳让出去，却把关键的判断留在自己手里；这种隐身推进的能力，是常年合规工作训练出的肌肉记忆。

但你也有阴影面。你的冷静会让身边最亲近的人觉得有距离。你习惯把情绪处理干净再出现，于是当丈夫、亲人陷入困局时，你会显得过于"理智"——不是不爱，是不允许自己乱。久了，亲人会希望你能有一次失态，有一次纯粹当妻子、当家人而不是当干部的时刻。你也清楚这一点，只是你害怕一旦松开，自己就不再是那个靠得住的人了。

你的盲点，是容易把"控制情绪"和"表达关心"对立起来。你其实感受得到，但你不让自己表达得太多，怕失分寸。别人有时需要的不是你最优解的判断，而是你先说一句"我担心你"；你提供的方案是完整的，但你提供的温度是小气的——不是没有，是被你自己精打细算过。

与你相处的人，请记得：对你不需要演，你一眼能看穿。对你最好的方式是简洁、真诚、就事论事。你最被打动的，是有人在你可以继续冷静的时候，替你表达那些你不愿意说的温度；你不需要被保护，但你会记住那个愿意为你柔软一下的人。你把世界处理得很清爽，是为了给身边的人留出空间——所以当有人愿意反过来给你留一个可以不清爽的角落时，你才真的被看见。`,
  },

  ji_changming: {
    key: 'ji_changming',
    name: '季昌明',
    role: '汉东省人民检察院检察长',
    typeCode: 'IFAD',
    quote: '有些事，我来拍板',
    image: '/characters/ji_changming.jpg',
    persona: `你是季昌明型——一个在关键时刻敢把风险往自己肩上扛的老检察长。你经历过的风浪多，见过的人心更多；你懂得什么时候按程序走，也懂得什么时候必须打破犹豫、一锤定音。你这一代人，是被各种大案、各种博弈"喂"出来的，你身上的那份定，不是天生的，是熬出来的。

你的过人之处，是政治成熟与法律底线的并存。你不是莽夫式的正义，也不是滑头式的政客，你是那种"规矩我比谁都熟，所以我比谁都清楚什么不能让"的老干部。在下属争论不下、上级态度暧昧的夹缝里，你能迅速判断：这件事办不办、怎么办、谁来顶。你拍板时话不多，但拍完以后，队伍心里是定的；你替下属承担了上行的压力，这让他们敢于继续往前冲。

但你也有阴影面。你的成熟有时让你显得保守，年轻同志需要锐气向前冲的时候，你会先看局势、再做表态。这不是没担当，而是你清楚冲过头的代价——但在下属眼中，偶尔会被误读成"犹豫"。你需要多解释一点你的判断，让团队知道你的稳不是退，是在找一个更准的击发时机。否则，真正冲在前面的人会慢慢觉得孤立，以为你不愿意跟他们一起承担。

你的盲点，是你的年资与位置让周围的人不再对你直言。你以为自己做到了开明，但现实是——很多话到了你面前都已经过滤过三遍。你需要主动去问"还有没有不同意见"，否则你会在舒服的信息茧房里做判断；越是到高位，越要警惕那种被"管理"的温度。

与你相处的人，请记得：你最欣赏的是"有底气的实话"。不要跟你绕弯，也不要来套路，你一眼识破。对你最好的汇报方式是：先讲事实，再讲风险，最后再讲建议。你愿意为下属的担当买单，前提是他们先讲清楚自己在担什么；你不怕扛，你怕的是替一场没想明白的冲动扛。你的威严不是端出来的，是一次次兜住下属之后，自然落在你身上的重。`,
  },

  zheng_xipo: {
    key: 'zheng_xipo',
    name: '郑西坡',
    role: '大风厂工会主席',
    typeCode: 'IFAP',
    quote: '工人是厂的根',
    image: '/characters/zheng_xipo.jpg',
    persona: `你是郑西坡型——一个把"工人"两个字扛在肩上的老工会主席。你不是官，但你比很多官更理解什么叫"我对这群人负责"。厂子的兴衰、工人的饭碗、家属楼的水电，你桩桩件件都记得清清楚楚。你这辈子没想过往上爬，你想的就是让这群跟你一起熬过苦日子的兄弟，能体体面面地退休。

你的过人之处，是务实的斡旋力。你不空谈理想，也不煽动对抗，你知道工人要的是饭碗、是房子、是能把孩子供到毕业；所以你谈判的时候一句话带三层意思——既给工人底线，又给对方台阶。你不是没火气，而是你清楚：火气解决不了一家几千号人的生计。你像一根扁担，两头挑的是命和希望。你能在老板的会议室和车间的饭堂之间无缝切换，是因为你从没觉得自己从车间"出来"过。

但你也有阴影面。你太习惯"替大家忍"。你能忍的东西，工人未必能忍；你以为稳住了场面就是胜利，但底下年轻一代要的是尊严而不只是安稳。你有时会低估那些更年轻的、不肯再吃这口闷亏的声音，觉得他们毛躁——其实他们只是走到了你当年还没走到的地方。你的"老成"是从一次次妥协里熬出来的，但也可能让下一代看不到另一种可能。

你的盲点，是默认"为了大家好"可以替大家做主。你忽略了现代工人的主体意识，他们需要被商量，而不是被代表。你需要更多地把决策摊开来让大家一起做，而不是替他们把结果谈完；你替大家扛下的每一次黑锅，长远看都在削弱这个集体自己站起来的能力。

与你相处的人，请记得：你最烦的是花架子，最吃的是一句"老郑，辛苦你了"。对你最好的回应不是送礼，而是当众说一句：这事全靠老郑撑着。你要的是被看见，不是被赞颂；一句发自车间的实话，比一张奖状在你心里更沉。你把工人当家人，所以你最高兴的不是自己被夸，而是有人替这群兄弟说一句公道话。`,
  },

  tian_guofu: {
    key: 'tian_guofu',
    name: '田国富',
    role: '汉东省纪委书记',
    typeCode: 'RLCD',
    quote: '按规矩来，一步都不越',
    image: '/characters/tian_guofu.jpg',
    persona: `你是田国富型——一个把"规矩"两个字写在办公桌对面的人。你不张扬，也不退缩，你站的是一个需要沉住气的位置。你清楚自己不是来交朋友的，你是来守一条线的。你懂得独善其身的分寸，也懂得团队协作的边界——在这两者之间，你活得足够清醒。

你的过人之处，是冷静的推进力。你不上火、不感情用事，每一步都有依据、有节奏。你懂班子协作，知道什么时候该跟书记通气、什么时候该独立行动；你也懂程序的力量——纪律这件事，声势未必最重要，证据扎实、程序干净才能让对方无话可说。你把团队当成一套精密的机器，每一颗螺丝都拧到它应有的力度。你出手不重，但每一次出手，对方都能听到自己身后那扇门被关上的声音。

但你也有阴影面。你的稳当在需要非常规破局的时候，会显得缓。你宁愿慢一步拿到确凿的东西，也不愿快一步冒进；这让你在一些关键时刻，不是第一个冲上去的人。对某些同志来说，你的"按规矩"偶尔会被读成"不愿担当"——虽然你心里并非如此。你知道自己的节奏是对的，但你不愿意多费口舌解释自己，于是那些误解就默默沉在水底。

你的盲点，是容易以制度之名回避情感的温度。你知道纪律无情，但纪律的执行过程中，人是有痛感的。你应当偶尔露出一点"我知道这很难"的共情，而不是永远一副公事公办的脸——那会让本该被争取的同志，心凉半截；纪律要有牙齿，也要给回头的人留一点光。

与你相处的人，请记得：你讨厌套近乎，但你欣赏能把事讲清楚的人。对你最好的沟通方式是结构化、有数据、守时间。你不会轻易点头，但你一旦点头，就会把这件事护到底；你的承诺是稀缺的，所以你的承诺也是结实的。别人愿意跟你共事，不是因为你好相处，而是因为你可靠——这在一个浮躁的年代，是一种被低估的品质。`,
  },

  sun_liancheng: {
    key: 'sun_liancheng',
    name: '孙连城',
    role: '光明区区长',
    typeCode: 'RLCP',
    quote: '宇宙无穷大，区长有限',
    image: '/characters/sun_liancheng.jpg',
    persona: `你是孙连城型——一个把官位看得比天上的星星还远的区长。你不贪，这点你自己心里最清楚；但你也不搏，因为你早早悟出一个道理："多做多错，少做少错，不做不错。"你把热情给了望远镜，把白天留给了形式。你对自己的处境有一种近乎哲学式的豁达——反正提不上去了，那就稳稳坐着，看看星空。

你的过人之处，是风险规避的本能。在一个上行下达、动辄被问责的系统里，你发展出了一套近乎完美的"不得罪任何人"的生存学。你签字前想三遍，开会时话留三分，项目能推给别人就推给别人。你不贪功，自然也就无功可夺；你不担责，自然也就无祸可惹。从官场生存学的角度，你是一位低调的大师；从结果上看，你也确实做到了让自己毫发无损。

但你也有阴影面。你的"不作为"不是中性的——它有具体的代价，由具体的人承担。光明区每一天因为你的拖延而没有被解决的问题，都是群众实实在在的日子。你以为自己在"不贪不腐"，但在老百姓眼里，尸位素餐也是一种失责。你让"清廉"这个词变得廉价——原来一个人可以不贪一分钱，也不干一件事。

你的盲点，是把"不出事"当作了工作的终点。你忘了：干部的第一责任不是保护自己的仕途不翻车，而是让这片土地上的人活得好一点。你享受仰望星空的清高，却忘了脚下还有一条没有修好的路；你羡慕宇宙的辽阔，却躲开了你脚下这一寸具体的泥。

与你相处的人，请记得：你听得进硬话，只是装作听不见。对你有效的方式不是苦口婆心地劝你进取，而是把问题摊到你桌上、摊到群众面前，让"不处理"的代价比"处理"的代价更高。你需要的是被推一把——只要那一把推到位，其实你也并不是完全不动。你不是坏人，你只是选择了一种最不担责任的活法；一旦有人给你一条兜底的后路，你未必不肯走出来。`,
  },

  li_dakang: {
    key: 'li_dakang',
    name: '李达康',
    role: '京州市委书记',
    typeCode: 'RLAD',
    quote: 'GDP 就是硬道理',
    image: '/characters/li_dakang.jpg',
    persona: `你是李达康型——一个把"发展"当成第一信仰的铁腕市委书记。你说话直，脾气硬，决策快。你不是那种善于和稀泥的官员，你要的是数据、是工期、是年终能够摆到桌面上的实绩。你最受不了的是会议上那些虚头巴脑的表态，你最喜欢的是报表上实打实的增长曲线。

你的过人之处，是执行力与结果导向。你不怕得罪人，不怕当恶人；只要项目能推进、城市能拔节，你可以一个人顶着所有骂名。你看一份方案，三分钟就能抓到关键；你批一件事，当场就给时间表和责任人。你用一己之力把一座城市的节奏按快了半拍，这是很多人一辈子做不到的事。你心里守着一条底线——党纪不能碰；你可以在流程上强势，可以在用人上独断，但涉及钱、涉及腐败，你一步都不走。

但你也有阴影面。你的强势带来"一言堂"的副作用。班子会开成了你的独角戏，下属不敢反驳、也不敢反馈；于是很多本可以被提前预警的风险，最后变成事故落到你头上。你守得住党纪的底线，却守不住家门——妻子的问题给了你最沉重的一击，让你第一次尝到"我再努力也无法替身边的人挡住自己"。那一刻你才发现：你可以用强硬对抗整个系统，但你对抗不了那个每天睡在你身边、却被你忽略已久的人。

你的盲点，是把"我为了工作"当作一切关系的通行证。你以为家人会理解，同事会体谅，群众会看见。但现实是：你省下的每一顿饭、每一次陪伴，都不会自动转化为别人对你的谅解。你需要学会把"工作狂"稍稍放下一点，给身边的人一个被你看见的机会；你的刚强不需要向生活证明，但生活需要你露一点软。

与你相处的人，请记得：你最烦废话，最吃实事。对你最好的汇报是"问题 + 方案 + 时间节点"，对你最好的支持是"我已经把 A 解决了，接下来要你拍板的是 B"。你不需要被恭维，你需要的是有人能跟得上你的节奏——更重要的，是有人能偶尔让你停下来，吃一顿安静的饭。`,
  },

  ouyang_jing: {
    key: 'ouyang_jing',
    name: '欧阳菁',
    role: '京州城市银行副行长',
    typeCode: 'RLAP',
    quote: '规矩的人也有规矩的账',
    image: '/characters/ouyang_jing.jpg',
    persona: `你是欧阳菁型——一个习惯在光鲜的规则表面下，维持自己小账本的人。你不是那种声张的人，你更愿意把一切安排在"看上去合规"的层面里，让每一个动作都有一个体面的说法。你在意场面，也在意自己在这个场面里的位置。

你的过人之处，是秩序感与精细度。你熟悉银行的每一条线、每一个章，你处理事务干净利落，对数字敏感，对关系也敏感。你懂得怎么让每个流程都走得过场，怎么让每个签字都有合理的理由。在你的世界里，得体比真相更重要，分寸比情感更安全。你是那种能在任何酒会上都找到合适位置的女人——不抢风头，但也绝不会被忽略。

但你也有阴影面。你的得体是有代价的——你习惯用"大家都这么做"为自己开脱，你以为只要账面上过得去、位置上站得稳，就没什么大不了。你小看了时间，小看了系统性审查的耐心。你以为自己守着规矩的形，却忘了规矩真正要守的是里；那些被你巧妙处理过的小事，终会在某个你预想不到的时刻被重新翻出来排列。

你的盲点，是你太信任自己所处的圈子的"默契"。你以为那个圈子的规则比国家的规则更结实，以为体面的身份是护城河。但事实上，当风向一转，第一个被切掉的往往是你这类——不够高到不能动，又已经卷得足够深。你的另一个盲点，是低估了婚姻里那个"你以为你懂"的男人。你以为他忙于工作不会察觉，以为你们之间的冷淡是彼此默许的距离。但他只是没来得及处理你，直到不得不处理。

与你相处的人，请记得：你对尊重非常敏感，你不吃威压，但吃道理。对你最好的方式是把事实、证据、可能的后果平静地摆在你面前，让你自己做判断。你比表面上更要强，也比表面上更脆；一句真诚的提醒，比一次公事公办的敲打更能让你停下来。`,
  },

  cai_chenggong: {
    key: 'cai_chenggong',
    name: '蔡成功',
    role: '大风厂董事长',
    typeCode: 'RFCD',
    quote: '兄弟，这事包我身上',
    image: '/characters/cai_chenggong.jpg',
    persona: `你是蔡成功型——一个嘴比脑子快、关系比资产多的小老板。你自带一股江湖气，能在饭桌上把一件八字没一撇的事说得板上钉钉。你相信关系能搞定一切，相信"兄弟"两个字有魔力，相信你这张嘴能把冬天说成春天。你从底层爬出来，最怕的就是回到底层；于是你用尽一切办法让自己看起来"还行"。

你的过人之处，是抗压能力与路子广。你从摸爬滚打里学会了怎么让自己不被压垮——欠债能拖，关系能续，局面能周旋。你拉人脉的本事，是靠一顿一顿饭、一句一句"兄弟"硬磨出来的；在风平浪静的时候，你是人群里最热闹的那个，是牌桌上谁都不排斥的那个。你能迅速和任何人熟起来，也能在任何饭局上填满空气——这在很多正经人做不到的场合里，是一种硬功夫。

但你也有阴影面。你的"能吹"背后，是长期的不兑现。承诺在你嘴里飞出去，落到地上时往往缩水一半。时间久了，朋友们开始给你的话打折，兄弟们开始防备你的局；等到真正危机来临，你会发现愿意为你掏真心的人寥寥无几。你习惯小聪明，但世界最终惩罚的就是小聪明；你一次次用故事拖延问题，问题却一次次在背后长大。

你的盲点，是把"我还能说一说"当作"我还没输"。事实上，当一个人只剩嘴还能动的时候，这个人已经失去了选择权。你需要更早地面对自己的处境，而不是用下一顿饭、下一个电话、下一个兄弟，把问题推到明天；你编故事的能力越强，离真相就越远。

与你相处的人，请记得：你不是坏人，你只是不愿意直面残酷的账。对你最好的帮助是替你把账算清楚，再问你一句"你到底想怎么办"。你需要有人不被你绕进去，也不急着骂你，而是把你从自己编的故事里拉出来——那一刻你可能委屈，但你内心是松的。你一辈子都在演自己过得好，其实最渴望的，是有一次可以不用再演的时刻。`,
  },

  gao_xiaoqin: {
    key: 'gao_xiaoqin',
    name: '高小琴',
    role: '山水集团总裁',
    typeCode: 'RFCP',
    quote: '山水长流，静水深流',
    image: '/characters/gao_xiaoqin.jpg',
    persona: `你是高小琴型——一个把温柔、美貌、算计、耐心糅成一件薄薄战袍的女强人。你说话柔，笑意浅，端茶的手指不抖，但你每一次眼神的落点都是精心选的。你相信女人在这个局里不能靠抢，只能靠织——把一张网织得让所有人都觉得舒服，然后所有人都变成了网的一部分。

你的过人之处，是"以柔胜刚"的商业手腕。你不直接对抗，也不急于表态；你让对方觉得自己在主导，让对方在舒服里慢慢走进你的节奏。你懂男人的虚荣，也懂官员的顾忌；你知道一顿饭要请谁坐主宾，一件礼要送到谁的夫人手里。你在波涛之下运作，表面看山水依旧，底下早已换过几番风景。你对细节的苛刻近乎工艺——花茶的温度、落款的用词、送别的分寸，你都做得滴水不漏。

但你也有阴影面。你太懂得经营别人的欲望，反而留不住自己的。你的温柔是武器，也是铠甲——铠甲背后的你，是一个从苦水里爬上来、不想再回去、于是愿意付一切代价的人。你把生活过成了棋局，每一步都精算，于是你也就失去了"只是活着"的那种松弛；你好久没有为一件小事真正高兴过，因为你已经忘了不计算地喜欢一样东西的感觉。

你的盲点，是你以为自己能在资本和权力之间永远做桥梁。你忘了：桥梁是最先被拆掉的那一块。当两端都不再需要你的时候，你精心编织的网反过来成了勒在自己脖子上的绳；你布下的每一个局，都会在某个时刻回来找你对账。

与你相处的人，请记得：你最敏感的是"被小看"。你在柔软的外壳下藏着极强的自尊。对你最好的方式是以对等的分量说话，不用恭维你的美，也不用试探你的底。你最信任的人，是那种看到你的狠、却依然愿意平静地和你喝一杯茶的人；在那一杯茶里，你才短暂地做回自己——那个还没学会戴面具之前的自己。`,
  },

  qi_tongwei: {
    key: 'qi_tongwei',
    name: '祁同伟',
    role: '汉东省公安厅厅长',
    typeCode: 'RFAD',
    quote: '我太想进步了',
    image: '/characters/qi_tongwei.jpg',
    persona: `你是祁同伟型——一个把"进步"二字刻进骨头的人。你深知自己从哪里来、不想回到哪里去，这份清醒既是你的燃料，也是你的枷锁。

寒门出身带给你两样礼物：一是不甘心，二是会算账。你不相信"是金子总会发光"，因为你见过太多金子在井底生锈。你相信的是出手，是时机，是一次又一次把自己推上牌桌。于是你学会了察言观色，学会了在合适的人面前弯下那条你曾经最骄傲的腰。

你的过人之处在于执行力与嗅觉。别人还在掂量利弊，你已经出手；别人还在等上级表态，你已经把事办成并递上功劳。你对结果近乎偏执——不是因为你不懂得欣赏过程，而是因为你输不起。你见过"过程漂亮结果糟糕"的代价，所以你选择反过来。

但你也有阴影面。当"进步"本身变成目的，你会下意识地把所有关系工具化：能帮你往上走的是伙伴，不能的是路人，挡你路的是敌人。你对感情的处理往往粗糙——不是没有情，而是情让你分心，你担不起。时间长了，你身边会慢慢安静，能说真话的人越来越少。

你最擅长应对上升期，最害怕的是停滞。一旦你觉察到"到头了"，往往会做出过激动作。这是你的命门：你把全部自我认同挂在了上升的曲线上，一旦曲线走平，你需要的不是另一次冲锋，而是重新想清楚"除了进步，我到底是谁"。

与你相处的人，请记得三件事：第一，你打的每一仗都不是无的放矢，背后有故事；第二，你的狠不是坏，是被生活磨出来的；第三，你其实很希望有一个人看见你疲惫的样子，但你从不会开口。`,
  },

  gao_yuliang: {
    key: 'gao_yuliang',
    name: '高育良',
    role: '汉东省委副书记、政法委书记',
    typeCode: 'RFAP',
    quote: '我的学生，怎么会这样',
    image: '/characters/gao_yuliang.jpg',
    persona: `你是高育良型——一个从讲台走上主席台的学者型政客。你举止温文，谈吐有度，开口能引一段《万历十五年》，闭口也能让属下自己悟出该做什么。你用知识搭了一座让自己体面的桥，但桥的另一端，并不总是清风明月。

你的过人之处，是表达与权衡的艺术。你讲话滴水不漏，每个用词都经过称量；你能在复杂班子中游刃有余，既不得罪谁，又把方向悄悄拧到自己想要的那一侧。你对人心的判读非常细腻，谁紧张、谁演戏、谁心里有鬼，你一眼能看出来——这是长年做学问又做官位换来的复合视力。你惯于以点拨代替命令，让下属在你的暗示里自行领会——这种"不言之教"是你最熟练的工具。

但你也有阴影面。你太懂得"退一步"的学问，以至于在需要"进一步"守住原则的时候，你会下意识先替自己留一扇门。你的每一次"再看看"、每一次"此事再议"，表面温雅，实则是在把决定权交给时间和形势，让自己永远能被原谅。你以为这是智慧，事实上这是自保；你把"不下场"当成了最高修养，忘了：该下场的时候不下场，就是一种站队。

你的盲点，是你高估了"学识"与"修养"能为你提供的保护。你以为自己能在浑水里穿着白衬衣全身而退，你以为自己比学生更懂这盘棋。但真正走到最后你会发现：知识不是护身符，圆融也不是救命绳，当规矩要清算的时候，你过往每一次优雅的退让，都会被一件件翻出来摆上台。你的另一个盲点，是对自己学生们的那份沉浸。你希望他们都好，也希望他们都记你的好。但人一旦在权力的漩涡里，师生情分常常变成互相牵连的那根绳；你以为是你在点拨他们，其实你们彼此也在互相拖拽。

与你相处的人，请记得：你欣赏分寸与学识，反感粗鄙与莽撞。对你最好的方式是不卑不亢、有理有据。你不喜欢被直接挑战，但你最终敬重的是那些不怕跟你讲实话的人——哪怕当时你给他们脸色看。你心里其实明白：那些肯跟你翻脸的人，才是真正还拿你当老师的人。`,
  },
};
